import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { api } from '../utils/api'
import { useRef } from 'react'
import { Plus, Edit2, Trash2, X, Check, RefreshCw, Package, Search, Monitor, User, ChevronDown, ChevronUp, UserPlus, AlertTriangle, Upload, ImageIcon, FileText, FileSignature } from 'lucide-react'

type LicenseType = 'ASSET_BOUND' | 'USER_BOUND'
interface Assignment { id:string; assetId?:string; userId?:string; assignedAt:string; revokedAt?:string; notes?:string; asset?:{id:string;assetCode?:string;name:string}; user?:{id:string;username:string;displayName?:string} }
type SoftwareCategory = 'OS' | 'GENERAL'
interface Company { id:string; name:string }
interface Vendor { id:string; name:string }
interface LicenseImage { id:string; filename:string; url:string; size?:number; mimeType?:string; createdAt:string }
interface License {
  id:string; name:string; version?:string; licenseKey?:string; licenseType:LicenseType; softwareCategory:SoftwareCategory
  seats?:number; usedSeats:number; expiryDate?:string; notes?:string; companyId?:string; company?:Company
  vendorId?:string; vendor?:Vendor; contractNumber?:string; purchaseDate?:string; purchasePrice?:number
  contractStart?:string; contractEnd?:string; autoRenew?:boolean
  assignments:Assignment[]; images:LicenseImage[]
}
interface AssetOption { id:string; assetCode?:string; name:string }
interface UserOption  { id:string; username:string; displayName?:string; email?:string }

const ic = 'w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white'
const expiredClass = (d?:string) => !d?'':new Date(d)<new Date()?'text-red-600 font-semibold':new Date(d)<new Date(Date.now()+30*86400000)?'text-amber-600':'text-gray-500'
const fmtMoney = (n?:number) => n==null ? '' : `฿${n.toLocaleString()}`

// ─── Assign Modal ─────────────────────────────────────────────────────────────
function AssignModal({ license, onClose }: { license:License; onClose:()=>void }) {
  const qc = useQueryClient()
  const [targetId, setTargetId] = useState('')
  const [targetLabel, setTargetLabel] = useState('')
  const [notes, setNotes] = useState('')
  const [searchQ, setSearchQ] = useState('')
  const [open, setOpen] = useState(false)
  const isAsset = license.licenseType === 'ASSET_BOUND'

  const { data: assets=[] } = useQuery<AssetOption[]>({ queryKey:['assets-all'], queryFn:()=>api.get('/assets?limit=2000').then(r=>r.data.assets), enabled:isAsset })
  const { data: users=[]  } = useQuery<UserOption[]>({  queryKey:['users'],      queryFn:()=>api.get('/users').then(r=>r.data?.users || []),   enabled:!isAsset })

  const assignedIds = new Set(license.assignments.filter(a=>!a.revokedAt).map(a=>isAsset?a.assetId:a.userId))

  const mut = useMutation({
    mutationFn: () => api.post(`/software/${license.id}/assign`, isAsset ? { assetId: targetId, notes } : { userId: targetId, notes }),
    onSuccess: () => { qc.invalidateQueries({ queryKey:['software'] }); onClose() },
    onError: (e:any) => alert(e.response?.data?.message||'เกิดข้อผิดพลาด'),
  })

  const available = isAsset
    ? assets.filter(a=>!assignedIds.has(a.id))
    : users.filter((u:any)=>!assignedIds.has(u.id))

  const itemLabel = (item:any) => isAsset
    ? `${item.assetCode?`[${item.assetCode}] `:''}${item.name}`
    : `${item.displayName||item.username}${item.email?` (${item.email})`:''}`

  const filtered = available.filter(item =>
    !searchQ || itemLabel(item).toLowerCase().includes(searchQ.toLowerCase())
  )

  const select = (item:any) => { setTargetId(item.id); setTargetLabel(itemLabel(item)); setOpen(false); setSearchQ('') }

  const unlimited = license.seats == null
  const remaining = unlimited ? Infinity : license.seats! - license.usedSeats

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-md p-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h2 className="font-semibold text-gray-900 flex items-center gap-2">
              <UserPlus size={16} className="text-blue-600"/> ผูก Seat
            </h2>
            <p className="text-xs text-gray-400 mt-0.5">{license.name} · เหลือ {unlimited ? '∞' : remaining}/{unlimited ? '∞' : license.seats} seats</p>
          </div>
          <button onClick={onClose} className="p-1.5 hover:bg-gray-100 rounded-lg"><X size={16}/></button>
        </div>

        {remaining <= 0 && (
          <div className="bg-red-50 rounded-xl p-3 mb-4 flex items-center gap-2 text-sm text-red-700">
            <AlertTriangle size={14}/> Seat เต็มแล้ว ไม่สามารถผูกเพิ่มได้
          </div>
        )}

        <div className="space-y-3">
          <div>
            <label className="text-xs font-medium text-gray-600 block mb-1">
              เลือก {isAsset ? 'Asset (เครื่อง)' : 'User (ผู้ใช้งาน)'} *
            </label>
            <div className="relative">
              {/* trigger */}
              <button type="button" disabled={remaining<=0} onClick={()=>{ setOpen(o=>!o); setSearchQ('') }}
                className={`${ic} text-left flex items-center justify-between ${!targetId?'text-gray-400':''} ${remaining<=0?'opacity-50 cursor-not-allowed':''}`}>
                <span className="truncate">{targetLabel || `-- เลือก ${isAsset?'Asset':'User'} --`}</span>
                <ChevronDown size={14} className="flex-shrink-0 text-gray-400"/>
              </button>
              {/* dropdown */}
              {open && (
                <div className="absolute z-20 w-full mt-1 bg-white border border-gray-200 rounded-xl shadow-lg overflow-hidden">
                  <div className="p-2 border-b border-gray-100">
                    <input autoFocus value={searchQ} onChange={e=>setSearchQ(e.target.value)}
                      placeholder={`ค้นหา${isAsset?' asset code, ชื่อ...':' ชื่อ, email...'}`}
                      className="w-full px-3 py-1.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"/>
                  </div>
                  <div className="max-h-52 overflow-y-auto">
                    {filtered.length === 0
                      ? <p className="text-xs text-gray-400 text-center py-4">ไม่พบรายการ</p>
                      : filtered.map(item => (
                          <button key={item.id} type="button" onClick={()=>select(item)}
                            className={`w-full text-left px-3 py-2 text-sm hover:bg-blue-50 transition-colors ${item.id===targetId?'bg-blue-50 text-blue-700 font-medium':'text-gray-700'}`}>
                            {isAsset ? (
                              <span>
                                {(item as AssetOption).assetCode && <span className="font-mono text-blue-600 mr-1.5">[{(item as AssetOption).assetCode}]</span>}
                                {item.name}
                              </span>
                            ) : itemLabel(item)}
                          </button>
                        ))
                    }
                  </div>
                </div>
              )}
            </div>
            {available.length === 0 && remaining > 0 && (
              <p className="text-xs text-gray-400 mt-1">{isAsset?'ไม่มี Asset ให้เลือก':'ไม่มี User ให้เลือก'}</p>
            )}
          </div>
          <div>
            <label className="text-xs font-medium text-gray-600 block mb-1">หมายเหตุ</label>
            <input value={notes} onChange={e=>setNotes(e.target.value)} className={ic} placeholder="ระบุเพิ่มเติม..."/>
          </div>
          <div className="flex gap-2 pt-1">
            <button onClick={onClose} className="flex-1 px-4 py-2 border border-gray-200 rounded-xl text-sm text-gray-700 hover:bg-gray-50">ยกเลิก</button>
            <button onClick={()=>mut.mutate()} disabled={!targetId||remaining<=0||mut.isPending}
              className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-xl text-sm font-medium hover:bg-blue-700 disabled:opacity-50 flex items-center justify-center gap-2">
              {mut.isPending?<RefreshCw size={14} className="animate-spin"/>:<Check size={14}/>} ผูก Seat
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

// ─── File Grid (images + PDFs) ────────────────────────────────────────────────
function LicenseFileGrid({ files, isAdmin, onDelete, onLightbox }: {
  files: LicenseImage[]; isAdmin?: boolean;
  onDelete: (id: string) => void;
  onLightbox: (url: string) => void;
}) {
  if (files.length === 0) return <p className="text-xs text-gray-400 text-center py-3">ยังไม่มีไฟล์ — กด "เพิ่มไฟล์" เพื่ออัพโหลด</p>
  return (
    <div className="grid grid-cols-4 gap-2">
      {files.map(f => {
        const isPdf = f.mimeType === 'application/pdf'
        return (
          <div key={f.id} className="relative group aspect-square">
            {isPdf ? (
              <a href={f.url} target="_blank" rel="noreferrer"
                className="w-full h-full flex flex-col items-center justify-center gap-1 bg-red-50 border border-red-100 rounded-lg hover:border-red-300 transition-colors cursor-pointer">
                <FileText size={22} className="text-red-500"/>
                <span className="text-xs text-red-600 font-medium px-1 truncate w-full text-center leading-tight">
                  {f.filename.replace(/^\d+-[a-z0-9]+\./, '').slice(0, 12) || 'PDF'}
                </span>
              </a>
            ) : (
              <img src={f.url} alt={f.filename} onClick={() => onLightbox(f.url)}
                className="w-full h-full object-cover rounded-lg cursor-pointer border border-gray-100 hover:border-blue-300 transition-colors"/>
            )}
            {isAdmin && (
              <button type="button" onClick={() => { if (confirm('ลบไฟล์นี้?')) onDelete(f.id) }}
                className="absolute top-1 right-1 p-0.5 bg-red-500 text-white rounded opacity-0 group-hover:opacity-100 transition-opacity">
                <X size={10}/>
              </button>
            )}
          </div>
        )
      })}
    </div>
  )
}

// ─── License Form Modal ───────────────────────────────────────────────────────
function LicenseModal({ license, allLicenses=[], onClose, onSave }: { license?:License|null; allLicenses?:License[]; onClose:()=>void; onSave:(d:any)=>void }) {
  const qc = useQueryClient()
  const [form, setForm] = useState({
    name: license?.name||'', version: license?.version||'',
    licenseKey: license?.licenseKey||'',
    licenseType: license?.licenseType||'ASSET_BOUND' as LicenseType,
    softwareCategory: license?.softwareCategory||'GENERAL' as SoftwareCategory,
    seats: license?.seats?.toString()||'',
    expiryDate: license?.expiryDate?.slice(0,10)||'',
    notes: license?.notes||'',
    companyId: license?.companyId||'',
    vendorId: license?.vendorId||'',
    contractNumber: license?.contractNumber||'',
    purchaseDate: license?.purchaseDate?.slice(0,10)||'',
    purchasePrice: license?.purchasePrice?.toString()||'',
    contractStart: license?.contractStart?.slice(0,10)||'',
    contractEnd: license?.contractEnd?.slice(0,10)||'',
    autoRenew: license?.autoRenew||false,
  })
  const [templateSearch, setTemplateSearch] = useState('')
  const [templateOpen, setTemplateOpen] = useState(false)

  const softwareTemplates = allLicenses.reduce<{key:string; label:string; l:License}[]>((acc, l) => {
    const key = [l.name, l.version||'', l.softwareCategory, l.licenseType, l.companyId||''].join('|')
    if (!acc.find(t => t.key === key)) {
      const label = [l.name, l.version?`v${l.version}`:'', l.softwareCategory==='OS'?'OS':'App', l.company?.name||''].filter(Boolean).join(' · ')
      acc.push({ key, label, l })
    }
    return acc
  }, [])

  const filteredTemplates = templateSearch
    ? softwareTemplates.filter(t => t.label.toLowerCase().includes(templateSearch.toLowerCase()))
    : softwareTemplates

  const applyTemplate = (l: License) => {
    setForm(f => ({ ...f, name:l.name, version:l.version||'', licenseType:l.licenseType, softwareCategory:l.softwareCategory, companyId:l.companyId||'', expiryDate:l.expiryDate?.slice(0,10)||'' }))
    setTemplateOpen(false)
    setTemplateSearch('')
  }
  const [imgError, setImgError] = useState<string|null>(null)
  const [lightboxImg, setLightboxImg] = useState<string|null>(null)
  const imgFileRef = useRef<HTMLInputElement>(null)
  const set = (k:string) => (e:React.ChangeEvent<HTMLInputElement|HTMLSelectElement|HTMLTextAreaElement>) => setForm(f=>({...f,[k]:e.target.value}))
  const { data: companies=[] } = useQuery<Company[]>({ queryKey:['masterdata','companies'], queryFn:()=>api.get('/masterdata/companies').then(r=>r.data).catch(()=>[]) })
  const { data: vendors=[] } = useQuery<Vendor[]>({ queryKey:['vendors'], queryFn:()=>api.get('/vendors').then(r=>r.data).catch(()=>[]) })

  const uploadImgMut = useMutation({
    mutationFn: (files: File[]) => {
      const fd = new FormData()
      files.forEach(f => fd.append('images', f))
      return api.post(`/software/${license!.id}/images`, fd)
    },
    onSuccess: () => { qc.invalidateQueries({ queryKey:['software'] }); setImgError(null) },
    onError: (e:any) => setImgError(e?.response?.data?.message || 'อัพโหลดไม่สำเร็จ'),
  })
  const deleteImgMut = useMutation({
    mutationFn: (imageId:string) => api.delete(`/software/images/${imageId}`),
    onSuccess: () => qc.invalidateQueries({ queryKey:['software'] }),
  })

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-lg flex flex-col max-h-[90vh]">
        <div className="flex items-center justify-between p-5 border-b border-gray-100 flex-shrink-0">
          <h2 className="font-semibold text-gray-900">{license?'แก้ไข':'เพิ่ม'} Software License</h2>
          <button onClick={onClose} className="p-1.5 hover:bg-gray-100 rounded-lg"><X size={16}/></button>
        </div>
        <form onSubmit={e=>{e.preventDefault();onSave({...form,seats:form.seats===''?null:form.seats})}} className="flex flex-col flex-1 min-h-0">
          <div className="overflow-y-auto flex-1 p-5 space-y-4">
            {/* Template picker — create mode only */}
            {!license && softwareTemplates.length > 0 && (
              <div className="bg-blue-50 border border-blue-100 rounded-xl p-3">
                <p className="text-xs font-medium text-blue-700 mb-2">คัดลอกข้อมูลจากซอฟต์แวร์ที่มีอยู่</p>
                <div className="relative">
                  <button type="button" onClick={()=>setTemplateOpen(o=>!o)}
                    className="w-full text-left flex items-center justify-between px-3 py-2 bg-white border border-blue-200 rounded-lg text-sm text-gray-600 hover:border-blue-400 transition-colors">
                    <span className="truncate text-gray-400">เลือกซอฟต์แวร์ต้นแบบ...</span>
                    <ChevronDown size={14} className="flex-shrink-0 text-gray-400"/>
                  </button>
                  {templateOpen && (
                    <div className="absolute z-20 w-full mt-1 bg-white border border-gray-200 rounded-xl shadow-lg overflow-hidden">
                      <div className="p-2 border-b border-gray-100">
                        <input autoFocus value={templateSearch} onChange={e=>setTemplateSearch(e.target.value)}
                          placeholder="ค้นหาชื่อโปรแกรม..." className="w-full px-3 py-1.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"/>
                      </div>
                      <div className="max-h-52 overflow-y-auto">
                        {filteredTemplates.length === 0
                          ? <p className="text-xs text-gray-400 text-center py-4">ไม่พบ</p>
                          : filteredTemplates.map(t => (
                            <button key={t.key} type="button" onClick={()=>applyTemplate(t.l)}
                              className="w-full text-left px-3 py-2.5 text-sm hover:bg-blue-50 transition-colors border-b border-gray-50 last:border-0">
                              <p className="font-medium text-gray-800 truncate">{t.l.name}</p>
                              <p className="text-xs text-gray-400 mt-0.5 truncate">
                                {[t.l.version?`v${t.l.version}`:'', t.l.softwareCategory==='OS'?'OS':'App', t.l.licenseType==='ASSET_BOUND'?'Asset Bound':'User Bound', t.l.company?.name].filter(Boolean).join(' · ')}
                              </p>
                            </button>
                          ))
                        }
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}
            <div><label className="text-xs font-medium text-gray-600 block mb-1">ชื่อโปรแกรม *</label><input value={form.name} onChange={set('name')} className={ic} required placeholder="Microsoft 365..."/></div>
            <div>
              <label className="text-xs font-medium text-gray-600 block mb-1">ประเภทโปรแกรม</label>
              <div className="grid grid-cols-2 gap-2">
                {([['OS','ระบบปฏิบัติการ','Windows, macOS, Linux...'],['GENERAL','โปรแกรมทั่วไป','Office, Adobe, Antivirus...']] as [SoftwareCategory,string,string][]).map(([val,label,hint])=>(
                  <button key={val} type="button" onClick={()=>setForm(f=>({...f,softwareCategory:val}))}
                    className={`p-2.5 rounded-xl border-2 text-left text-xs transition-colors ${form.softwareCategory===val?'border-indigo-500 bg-indigo-50 text-indigo-700':'border-gray-200 text-gray-600 hover:border-gray-300'}`}>
                    <p className="font-semibold">{label}</p><p className="opacity-60 mt-0.5">{hint}</p>
                  </button>
                ))}
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><label className="text-xs font-medium text-gray-600 block mb-1">Version</label><input value={form.version} onChange={set('version')} className={ic} placeholder="2024..."/></div>
              <div><label className="text-xs font-medium text-gray-600 block mb-1">จำนวน Seat <span className="font-normal text-gray-400">(เว้นว่าง = ไม่จำกัด)</span></label><input value={form.seats} onChange={set('seats')} className={ic} type="number" min="1" placeholder="∞ ไม่จำกัด"/></div>
            </div>
            <div><label className="text-xs font-medium text-gray-600 block mb-1">License Key</label><input value={form.licenseKey} onChange={set('licenseKey')} className={ic} placeholder="XXXXX-XXXXX-..."/></div>
            <div>
              <label className="text-xs font-medium text-gray-600 block mb-1">ประเภท License</label>
              <div className="grid grid-cols-2 gap-2">
                {(['ASSET_BOUND','USER_BOUND'] as LicenseType[]).map(t=>(
                  <button key={t} type="button" onClick={()=>setForm(f=>({...f,licenseType:t}))}
                    className={`p-3 rounded-xl border-2 text-sm flex items-center gap-2 transition-colors ${form.licenseType===t?'border-blue-500 bg-blue-50 text-blue-700':'border-gray-200 text-gray-600 hover:border-gray-300'}`}>
                    {t==='ASSET_BOUND'?<Monitor size={14}/>:<User size={14}/>}
                    <div className="text-left">
                      <p className="font-medium text-xs">{t==='ASSET_BOUND'?'Asset Bound':'User Bound'}</p>
                      <p className="text-xs opacity-60">{t==='ASSET_BOUND'?'ผูกกับเครื่อง':'ผูกกับบุคคล'}</p>
                    </div>
                  </button>
                ))}
              </div>
            </div>
            <div><label className="text-xs font-medium text-gray-600 block mb-1">บริษัท</label>
              <select value={form.companyId} onChange={set('companyId')} className={ic}>
                <option value="">— ไม่ระบุ —</option>
                {companies.map(c=><option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
            </div>
            <div><label className="text-xs font-medium text-gray-600 block mb-1">วันหมดอายุ</label><input type="date" value={form.expiryDate} onChange={set('expiryDate')} className={ic}/></div>

            {/* Contract / Vendor section */}
            <div className="border border-gray-200 rounded-xl p-3 space-y-3">
              <p className="text-xs font-semibold text-gray-600 flex items-center gap-1.5"><FileSignature size={13}/> ข้อมูลสัญญา / ผู้ขาย</p>
              <div><label className="text-xs font-medium text-gray-600 block mb-1">Vendor / ผู้ขาย</label>
                <select value={form.vendorId} onChange={set('vendorId')} className={ic}>
                  <option value="">— ไม่ระบุ —</option>
                  {vendors.map(v=><option key={v.id} value={v.id}>{v.name}</option>)}
                </select>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div><label className="text-xs font-medium text-gray-600 block mb-1">เลขที่สัญญา / PO</label><input value={form.contractNumber} onChange={set('contractNumber')} className={ic} placeholder="PO-2569-..."/></div>
                <div><label className="text-xs font-medium text-gray-600 block mb-1">มูลค่าสัญญา (บาท)</label><input type="number" min="0" step="0.01" value={form.purchasePrice} onChange={set('purchasePrice')} className={ic} placeholder="0.00"/></div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div><label className="text-xs font-medium text-gray-600 block mb-1">วันที่ซื้อ</label><input type="date" value={form.purchaseDate} onChange={set('purchaseDate')} className={ic}/></div>
                <div/>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div><label className="text-xs font-medium text-gray-600 block mb-1">วันเริ่มสัญญา</label><input type="date" value={form.contractStart} onChange={set('contractStart')} className={ic}/></div>
                <div><label className="text-xs font-medium text-gray-600 block mb-1">วันสิ้นสุดสัญญา</label><input type="date" value={form.contractEnd} onChange={set('contractEnd')} className={ic}/></div>
              </div>
              <label className="flex items-center gap-2 text-xs text-gray-600 cursor-pointer">
                <input type="checkbox" checked={form.autoRenew} onChange={e=>setForm(f=>({...f,autoRenew:e.target.checked}))} className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"/>
                ต่ออายุสัญญาอัตโนมัติ (Auto-renew)
              </label>
            </div>

            <div><label className="text-xs font-medium text-gray-600 block mb-1">หมายเหตุ</label><textarea value={form.notes} onChange={set('notes')} rows={2} className={ic}/></div>

            {/* File section — edit mode only */}
            {license && (
              <div className="border border-gray-200 rounded-xl p-3">
                <div className="flex items-center justify-between mb-2">
                  <label className="text-xs font-medium text-gray-600 flex items-center gap-1.5"><ImageIcon size={12}/> รูปภาพ / ไฟล์ ({(license.images||[]).length})</label>
                  <div className="flex items-center gap-2">
                    {imgError && <span className="text-xs text-red-500">{imgError}</span>}
                    <input ref={imgFileRef} type="file" accept="image/jpeg,image/png,image/webp,application/pdf" multiple className="hidden"
                      onChange={e=>{ setImgError(null); const files=Array.from(e.target.files||[]); e.target.value=''; if(files.length) uploadImgMut.mutate(files) }}/>
                    <button type="button" onClick={()=>imgFileRef.current?.click()} disabled={uploadImgMut.isPending}
                      className="flex items-center gap-1 px-2.5 py-1.5 bg-blue-600 text-white rounded-lg text-xs font-medium hover:bg-blue-700 disabled:opacity-50">
                      {uploadImgMut.isPending?<RefreshCw size={11} className="animate-spin"/>:<Upload size={11}/>} เพิ่มไฟล์
                    </button>
                  </div>
                </div>
                <LicenseFileGrid files={license.images||[]} isAdmin
                  onDelete={id=>deleteImgMut.mutate(id)}
                  onLightbox={url=>setLightboxImg(url)}/>
              </div>
            )}
            {!license && (
              <div className="flex items-center gap-2 text-xs text-gray-400 bg-gray-50 rounded-xl px-3 py-2">
                <ImageIcon size={12}/> บันทึกก่อน แล้วเปิดแก้ไขเพื่อเพิ่มรูปภาพ / PDF
              </div>
            )}
          </div>
          <div className="flex gap-2 p-5 pt-3 border-t border-gray-100 flex-shrink-0">
            <button type="button" onClick={onClose} className="flex-1 px-4 py-2 border border-gray-200 rounded-xl text-sm text-gray-700 hover:bg-gray-50">ยกเลิก</button>
            <button type="submit" className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-xl text-sm font-medium hover:bg-blue-700 flex items-center justify-center gap-2"><Check size={14}/> บันทึก</button>
          </div>
        </form>
      </div>
      {lightboxImg && (
        <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4" onClick={()=>setLightboxImg(null)}>
          <img src={lightboxImg} alt="" className="max-w-full max-h-full rounded-xl shadow-2xl object-contain"/>
        </div>
      )}
    </div>
  )
}

// ─── License Card ─────────────────────────────────────────────────────────────
function LicenseCard({ l, isAdmin, onEdit, onDelete, forceExpand, highlightAsset }: { l:License; isAdmin?:boolean; onEdit?:()=>void; onDelete?:()=>void; forceExpand?:boolean; highlightAsset?:string }) {
  const qc = useQueryClient()
  const [expanded, setExpanded] = useState(false)
  const [showAssign, setShowAssign] = useState(false)
  const [lightboxImg, setLightboxImg] = useState<string|null>(null)
  const [imgError, setImgError] = useState<string|null>(null)
  const [assignSearch, setAssignSearch] = useState('')
  const imgFileRef = useRef<HTMLInputElement>(null)
  const isExpanded = expanded || (forceExpand ?? false)

  const revokeMut = useMutation({
    mutationFn: (assignId:string) => api.delete(`/software/${l.id}/assign/${assignId}`),
    onSuccess: () => qc.invalidateQueries({ queryKey:['software'] }),
    onError: (e:any) => alert(e.response?.data?.message||'เกิดข้อผิดพลาด'),
  })

  const uploadImgMut = useMutation({
    mutationFn: (files: File[]) => {
      const fd = new FormData()
      files.forEach(f => fd.append('images', f))
      return api.post(`/software/${l.id}/images`, fd)
    },
    onSuccess: () => { qc.invalidateQueries({ queryKey:['software'] }); setImgError(null) },
    onError: (e:any) => setImgError(e?.response?.data?.message || 'อัพโหลดไม่สำเร็จ'),
  })

  const deleteImgMut = useMutation({
    mutationFn: (imageId:string) => api.delete(`/software/images/${imageId}`),
    onSuccess: () => qc.invalidateQueries({ queryKey:['software'] }),
  })

  const isAsset = l.licenseType === 'ASSET_BOUND'
  const pct = l.seats ? Math.round((l.usedSeats / l.seats) * 100) : 0
  const allActiveAssignments = l.assignments.filter(a=>!a.revokedAt)
  const activeAssignments = highlightAsset && isAsset
    ? allActiveAssignments.filter(a =>
        (a.asset?.name||'').toLowerCase().includes(highlightAsset.toLowerCase()) ||
        (a.asset?.assetCode||'').toLowerCase().includes(highlightAsset.toLowerCase())
      )
    : allActiveAssignments
  const full = l.seats ? l.usedSeats >= l.seats : false
  const aq = assignSearch.trim().toLowerCase()
  const visibleAssignments = !aq ? activeAssignments : activeAssignments.filter(a => isAsset
    ? (a.asset?.name||'').toLowerCase().includes(aq) || (a.asset?.assetCode||'').toLowerCase().includes(aq)
    : (a.user?.displayName||'').toLowerCase().includes(aq) || (a.user?.username||'').toLowerCase().includes(aq))

  return (
    <div className="bg-white border border-gray-100 rounded-xl shadow-sm overflow-hidden">
      {/* Header row */}
      <div className="flex items-center gap-3 p-4">
        <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${isAsset?'bg-blue-50':'bg-purple-50'}`}>
          {isAsset?<Monitor size={17} className="text-blue-600"/>:<User size={17} className="text-purple-600"/>}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-sm font-semibold text-gray-900">{l.name}</span>
            <span className={`text-xs px-1.5 py-0.5 rounded font-medium ${l.softwareCategory==='OS'?'bg-indigo-100 text-indigo-700':'bg-gray-100 text-gray-500'}`}>
              {l.softwareCategory==='OS'?'OS':'App'}
            </span>
            {l.version && <span className="text-xs text-gray-400">v{l.version}</span>}
            {l.company && <span className="text-xs bg-purple-50 text-purple-700 px-1.5 py-0.5 rounded border border-purple-100">{l.company.name}</span>}
            {l.vendor && <span className="text-xs bg-amber-50 text-amber-700 px-1.5 py-0.5 rounded border border-amber-100 flex items-center gap-1"><FileSignature size={10}/>{l.vendor.name}</span>}
            {l.expiryDate && <span className={`text-xs ${expiredClass(l.expiryDate)}`}>หมด {new Date(l.expiryDate).toLocaleDateString('th-TH')}</span>}
            {l.contractEnd && <span className={`text-xs ${expiredClass(l.contractEnd)}`}>สัญญาถึง {new Date(l.contractEnd).toLocaleDateString('th-TH')}</span>}
          </div>
          {/* Seat progress bar */}
          <div className="flex items-center gap-2 mt-1.5">
            <div className="flex-1 bg-gray-100 rounded-full h-1.5">
              <div className={`h-1.5 rounded-full transition-all ${full?'bg-red-500':pct>75?'bg-amber-500':'bg-blue-500'}`} style={{width:`${Math.min(pct,100)}%`}}/>
            </div>
            <span className={`text-xs font-medium flex-shrink-0 ${full?'text-red-600':pct>75?'text-amber-600':'text-gray-600'}`}>
              {l.usedSeats}/{l.seats||'∞'} seats
            </span>
          </div>
        </div>
        <div className="flex items-center gap-1 flex-shrink-0">
          {isAdmin && onEdit && (
            <button onClick={onEdit} className="p-1.5 bg-white border border-gray-200 rounded-lg text-gray-400 hover:text-blue-500 shadow-sm"><Edit2 size={12}/></button>
          )}
          {isAdmin && onDelete && (
            <button onClick={onDelete} className="p-1.5 bg-white border border-gray-200 rounded-lg text-gray-400 hover:text-red-500 shadow-sm"><Trash2 size={12}/></button>
          )}
          {isAdmin && !full && (
            <button onClick={()=>setShowAssign(true)}
              className="flex items-center gap-1.5 px-2.5 py-1.5 bg-blue-600 text-white rounded-lg text-xs font-medium hover:bg-blue-700">
              <UserPlus size={12}/> ผูก Seat
            </button>
          )}
          {full && <span className="text-xs bg-red-100 text-red-600 px-2 py-1 rounded-lg font-medium">เต็ม</span>}
          <button onClick={()=>setExpanded(e=>!e)} className="p-1.5 text-gray-400 hover:bg-gray-100 rounded-lg">
            {expanded?<ChevronUp size={14}/>:<ChevronDown size={14}/>}
          </button>
        </div>
      </div>

      {/* Expanded: list of assignments */}
      {isExpanded && (
        <div className="border-t border-gray-50 px-4 pb-4 pt-3 space-y-2">
          <div className="flex items-center justify-between mb-2 gap-2">
            <p className="text-xs font-semibold text-gray-500 flex-shrink-0">
              {isAsset?'เครื่องที่ผูกอยู่':'User ที่ผูกอยู่'} ({visibleAssignments.length}{aq ? ` จาก ${activeAssignments.length}` : ''}{highlightAsset && isAsset && !aq && activeAssignments.length !== allActiveAssignments.length ? ` จาก ${allActiveAssignments.length}` : ''})
            </p>
            {activeAssignments.length > 5 && (
              <div className="relative flex-1 max-w-[220px]">
                <Search size={11} className="absolute left-2 top-1/2 -translate-y-1/2 text-gray-400"/>
                <input value={assignSearch} onChange={e=>setAssignSearch(e.target.value)}
                  placeholder={isAsset?'ค้นหาเครื่อง...':'ค้นหา user...'}
                  className="w-full pl-6 pr-2 py-1 border border-gray-200 rounded-lg text-xs focus:outline-none focus:ring-2 focus:ring-blue-400"/>
              </div>
            )}
          </div>
          {activeAssignments.length === 0 && <p className="text-xs text-gray-400 text-center py-3">ยังไม่ได้ผูก seat</p>}
          {activeAssignments.length > 0 && visibleAssignments.length === 0 && <p className="text-xs text-gray-400 text-center py-3">ไม่พบรายการที่ค้นหา</p>}
          {visibleAssignments.map(a => {
            const assetLabel = `${a.asset?.assetCode?`[${a.asset.assetCode}] `:''}${a.asset?.name||'—'}`
            const isHighlighted = highlightAsset && isAsset && (
              (a.asset?.name||'').toLowerCase().includes(highlightAsset.toLowerCase()) ||
              (a.asset?.assetCode||'').toLowerCase().includes(highlightAsset.toLowerCase())
            )
            return (
            <div key={a.id} className={`flex items-center gap-2 p-2.5 rounded-lg text-xs ${isHighlighted?'bg-blue-50 border border-blue-200':'bg-gray-50'}`}>
              <div className={`w-6 h-6 rounded-md flex items-center justify-center ${isAsset?'bg-blue-100 text-blue-600':'bg-purple-100 text-purple-600'}`}>
                {isAsset?<Monitor size={11}/>:<User size={11}/>}
              </div>
              <div className="flex-1 min-w-0">
                {isAsset
                  ? <span className={`font-medium ${isHighlighted?'text-blue-700':'text-gray-800'}`}>{assetLabel}</span>
                  : <span className="font-medium text-gray-800">{a.user?.displayName||a.user?.username||'—'}</span>}
                <span className="text-gray-400 ml-1">· {new Date(a.assignedAt).toLocaleDateString('th-TH-u-ca-gregory')}</span>
              </div>
              {isAdmin && (
                <button onClick={()=>{if(confirm('ยืนยันการยกเลิก seat นี้?'))revokeMut.mutate(a.id)}}
                  className="px-2 py-1 text-xs border border-gray-200 rounded text-gray-500 hover:bg-red-50 hover:text-red-600 hover:border-red-200 transition-colors">
                  ยกเลิก
                </button>
              )}
            </div>
          )})}

          {/* Revoked history */}
          {l.assignments.filter(a=>a.revokedAt).length > 0 && (
            <details className="mt-2">
              <summary className="text-xs text-gray-400 cursor-pointer hover:text-gray-600">ดูประวัติที่ยกเลิกแล้ว ({l.assignments.filter(a=>a.revokedAt).length})</summary>
              <div className="mt-2 space-y-1">
                {l.assignments.filter(a=>a.revokedAt).map(a=>(
                  <div key={a.id} className="flex items-center gap-2 p-2 bg-gray-50 rounded text-xs opacity-50">
                    <span className="flex-1 line-through">{isAsset?a.asset?.name:a.user?.displayName||a.user?.username}</span>
                    <span className="text-gray-400">ยกเลิก {a.revokedAt&&new Date(a.revokedAt).toLocaleDateString('th-TH')}</span>
                  </div>
                ))}
              </div>
            </details>
          )}

          {/* Contract info */}
          {(l.vendor || l.contractNumber || l.purchaseDate || l.purchasePrice != null || l.contractStart || l.contractEnd) && (
            <div className="mt-3 pt-3 border-t border-gray-100">
              <p className="text-xs font-semibold text-gray-500 flex items-center gap-1.5 mb-2"><FileSignature size={11}/> ข้อมูลสัญญา / ผู้ขาย</p>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 text-xs">
                {l.vendor && <div><span className="text-gray-400">Vendor:</span> <span className="text-gray-700 font-medium">{l.vendor.name}</span></div>}
                {l.contractNumber && <div><span className="text-gray-400">เลขที่สัญญา:</span> <span className="text-gray-700 font-medium">{l.contractNumber}</span></div>}
                {l.purchasePrice != null && <div><span className="text-gray-400">มูลค่า:</span> <span className="text-gray-700 font-medium">{fmtMoney(l.purchasePrice)}</span></div>}
                {l.purchaseDate && <div><span className="text-gray-400">วันที่ซื้อ:</span> <span className="text-gray-700 font-medium">{new Date(l.purchaseDate).toLocaleDateString('th-TH')}</span></div>}
                {l.contractStart && <div><span className="text-gray-400">เริ่มสัญญา:</span> <span className="text-gray-700 font-medium">{new Date(l.contractStart).toLocaleDateString('th-TH')}</span></div>}
                {l.contractEnd && <div><span className="text-gray-400">สิ้นสุดสัญญา:</span> <span className={`font-medium ${expiredClass(l.contractEnd)}`}>{new Date(l.contractEnd).toLocaleDateString('th-TH')}</span></div>}
                {l.autoRenew && <div className="text-emerald-600 font-medium">ต่ออายุอัตโนมัติ</div>}
              </div>
            </div>
          )}

          {/* File gallery */}
          <div className="mt-3 pt-3 border-t border-gray-100">
            <div className="flex items-center justify-between mb-2">
              <p className="text-xs font-semibold text-gray-500 flex items-center gap-1.5"><ImageIcon size={11}/> รูปภาพ / ไฟล์ ({(l.images||[]).length})</p>
              {isAdmin && (
                <div className="flex items-center gap-1">
                  {imgError && <span className="text-xs text-red-500">{imgError}</span>}
                  <input ref={imgFileRef} type="file" accept="image/jpeg,image/png,image/webp,application/pdf" multiple className="hidden"
                    onChange={e => { setImgError(null); const files=Array.from(e.target.files||[]); e.target.value=''; if(files.length) uploadImgMut.mutate(files) }}/>
                  <button onClick={()=>imgFileRef.current?.click()} disabled={uploadImgMut.isPending}
                    className="flex items-center gap-1 px-2 py-1 text-xs border border-gray-200 rounded-lg text-gray-500 hover:bg-blue-50 hover:text-blue-600 hover:border-blue-200 disabled:opacity-50 transition-colors">
                    {uploadImgMut.isPending ? <RefreshCw size={10} className="animate-spin"/> : <Upload size={10}/>}
                    เพิ่มไฟล์
                  </button>
                </div>
              )}
            </div>
            <LicenseFileGrid files={l.images||[]} isAdmin={isAdmin}
              onDelete={id=>deleteImgMut.mutate(id)}
              onLightbox={url=>setLightboxImg(url)}/>
          </div>
        </div>
      )}

      {/* Lightbox */}
      {lightboxImg && (
        <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4" onClick={()=>setLightboxImg(null)}>
          <img src={lightboxImg} alt="" className="max-w-full max-h-full rounded-xl shadow-2xl object-contain"/>
        </div>
      )}

      {showAssign && <AssignModal license={l} onClose={()=>setShowAssign(false)}/>}
    </div>
  )
}

// ─── License Group ────────────────────────────────────────────────────────────
interface LicenseGroup {
  key: string
  name: string
  version?: string
  softwareCategory: SoftwareCategory
  licenseType: LicenseType
  company?: Company
  licenses: License[]
  totalSeats: number
  usedSeats: number
}

function groupLicenses(licenses: License[]): LicenseGroup[] {
  const map = new Map<string, LicenseGroup>()
  for (const l of licenses) {
    const key = [l.name, l.version||'', l.softwareCategory, l.licenseType, l.companyId||''].join('|')
    if (!map.has(key)) {
      map.set(key, { key, name:l.name, version:l.version, softwareCategory:l.softwareCategory, licenseType:l.licenseType, company:l.company, licenses:[], totalSeats:0, usedSeats:0 })
    }
    const g = map.get(key)!
    g.licenses.push(l)
    g.totalSeats += l.seats || 0
    g.usedSeats += l.usedSeats
  }
  return Array.from(map.values())
}

function LicenseGroupCard({ group, isAdmin, onEdit, onDelete, forceExpand, highlightAsset }: {
  group: LicenseGroup; isAdmin?: boolean
  onEdit?: (l: License) => void; onDelete?: (l: License) => void
  forceExpand?: boolean; highlightAsset?: string
}) {
  const [expanded, setExpanded] = useState(false)
  const isExpanded = expanded || (forceExpand ?? false)

  if (group.licenses.length === 1) {
    const l = group.licenses[0]
    return (
      <LicenseCard l={l} isAdmin={isAdmin}
        onEdit={onEdit ? () => onEdit(l) : undefined}
        onDelete={onDelete ? () => onDelete(l) : undefined}
        forceExpand={forceExpand} highlightAsset={highlightAsset}/>
    )
  }

  const isAsset = group.licenseType === 'ASSET_BOUND'
  const pct = group.totalSeats ? Math.round((group.usedSeats / group.totalSeats) * 100) : 0
  const full = group.totalSeats > 0 && group.usedSeats >= group.totalSeats
  const availableCount = group.licenses.filter(l => l.seats == null || l.usedSeats < l.seats).length

  const accentColor = isAsset ? 'border-l-blue-400' : 'border-l-purple-400'

  return (
    <div className={`bg-white border border-gray-200 border-l-4 ${accentColor} rounded-xl shadow-sm overflow-hidden`}>
      <div className="flex items-center gap-3 px-4 py-3">
        <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${isAsset?'bg-blue-100':'bg-purple-100'}`}>
          {isAsset?<Monitor size={17} className="text-blue-600"/>:<User size={17} className="text-purple-600"/>}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-sm font-bold text-gray-900">{group.name}</span>
            <span className={`text-xs px-1.5 py-0.5 rounded font-medium ${group.softwareCategory==='OS'?'bg-indigo-100 text-indigo-700':'bg-gray-100 text-gray-500'}`}>
              {group.softwareCategory==='OS'?'OS':'App'}
            </span>
            {group.version && <span className="text-xs text-gray-400">v{group.version}</span>}
            {group.company && <span className="text-xs bg-purple-50 text-purple-700 px-1.5 py-0.5 rounded border border-purple-100">{group.company.name}</span>}
            <span className={`text-xs px-2 py-0.5 rounded-full font-semibold ${isAsset?'bg-blue-100 text-blue-700':'bg-purple-100 text-purple-700'}`}>{group.licenses.length} licenses</span>
            {availableCount > 0 && <span className="text-xs text-emerald-600 font-medium">{availableCount} ว่าง</span>}
          </div>
          <div className="flex items-center gap-2 mt-1.5">
            <div className="flex-1 bg-gray-100 rounded-full h-2">
              <div className={`h-2 rounded-full transition-all ${full?'bg-red-500':pct>75?'bg-amber-500':'bg-blue-500'}`} style={{width:`${Math.min(pct,100)}%`}}/>
            </div>
            <span className={`text-xs font-semibold flex-shrink-0 ${full?'text-red-600':pct>75?'text-amber-600':'text-gray-700'}`}>
              {group.usedSeats}/{group.totalSeats} seats รวม
            </span>
          </div>
        </div>
        <button onClick={()=>setExpanded(e=>!e)} className="p-1.5 text-gray-400 hover:bg-gray-100 rounded-lg">
          {isExpanded?<ChevronUp size={14}/>:<ChevronDown size={14}/>}
        </button>
      </div>
      {isExpanded && (
        <div className={`border-t-2 ${isAsset?'border-blue-100 bg-blue-50/40':'border-purple-100 bg-purple-50/40'} px-3 pb-3 pt-2 space-y-2`}>
          {group.licenses.map(l => (
            <LicenseCard key={l.id} l={l} isAdmin={isAdmin}
              onEdit={onEdit ? () => onEdit(l) : undefined}
              onDelete={onDelete ? () => onDelete(l) : undefined}
              forceExpand={forceExpand} highlightAsset={highlightAsset}/>
          ))}
        </div>
      )}
    </div>
  )
}

// ─── Main Page ────────────────────────────────────────────────────────────────
export default function SoftwarePage() {
  const qc = useQueryClient()
  const [showForm, setShowForm] = useState(false)
  const [editItem, setEditItem] = useState<License|null>(null)
  const [search, setSearch] = useState('')
  const [filterType, setFilterType] = useState('')
  const [filterCompany, setFilterCompany] = useState('')
  const isAdmin = true // TODO: get from auth context if needed

  const { data: companies=[] } = useQuery<Company[]>({ queryKey:['masterdata','companies'], queryFn:()=>api.get('/masterdata/companies').then(r=>r.data).catch(()=>[]) })

  const { data: licenses=[], isLoading } = useQuery<License[]>({
    queryKey: ['software', filterType, filterCompany],
    queryFn: () => {
      const p = new URLSearchParams()
      if (filterType)    p.set('licenseType', filterType)
      if (filterCompany) p.set('companyId',   filterCompany)
      return api.get(`/software${p.toString()?`?${p}`:''}`).then(r=>r.data)
    },
  })

  const createMut = useMutation({ mutationFn:(d:any)=>api.post('/software',d), onSuccess:()=>{qc.invalidateQueries({queryKey:['software']});setShowForm(false)}, onError:(e:any)=>alert(e.response?.data?.message||'เกิดข้อผิดพลาด') })
  const updateMut = useMutation({ mutationFn:({id,...d}:any)=>api.put(`/software/${id}`,d), onSuccess:()=>{qc.invalidateQueries({queryKey:['software']});setEditItem(null)}, onError:(e:any)=>alert(e.response?.data?.message||'เกิดข้อผิดพลาด') })
  const deleteMut = useMutation({ mutationFn:(id:string)=>api.delete(`/software/${id}`), onSuccess:()=>qc.invalidateQueries({queryKey:['software']}) })

  const q = search.toLowerCase()
  const matchesSoftware = (l:License) => !search || (l.name+(l.version||'')+(l.licenseKey||'')).toLowerCase().includes(q)
  const matchesAsset    = (l:License) => !!search && l.assignments.some(a => !a.revokedAt && (
    (a.asset?.name||'').toLowerCase().includes(q) ||
    (a.asset?.assetCode||'').toLowerCase().includes(q)
  ))
  const filtered = licenses.filter(l => matchesSoftware(l) || matchesAsset(l))
  const assetMatchedIds = new Set(filtered.filter(l => !matchesSoftware(l) && matchesAsset(l)).map(l=>l.id))
  const assetBound = filtered.filter(l=>l.licenseType==='ASSET_BOUND')
  const userBound  = filtered.filter(l=>l.licenseType==='USER_BOUND')

  return (
    <div className="p-6 max-w-5xl mx-auto">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-xl font-bold text-gray-900 flex items-center gap-2"><Package size={22}/> Software Licenses</h1>
          <p className="text-sm text-gray-500 mt-0.5">จัดการ license และการผูก seat</p>
        </div>
        <button onClick={()=>setShowForm(true)} className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-xl text-sm font-medium hover:bg-blue-700"><Plus size={15}/> เพิ่ม License</button>
      </div>

      {/* Summary */}
      <div className="grid grid-cols-3 gap-4 mb-5">
        <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-4 text-center">
          <p className="text-2xl font-bold text-gray-900">{licenses.length}</p>
          <p className="text-xs text-gray-500 mt-0.5">License ทั้งหมด</p>
        </div>
        <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-4 text-center">
          <p className="text-2xl font-bold text-blue-600">{licenses.filter(l=>l.licenseType==='ASSET_BOUND').reduce((s,l)=>s+l.usedSeats,0)}</p>
          <p className="text-xs text-gray-500 mt-0.5">Seat ที่ผูกกับเครื่อง</p>
        </div>
        <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-4 text-center">
          <p className="text-2xl font-bold text-purple-600">{licenses.filter(l=>l.licenseType==='USER_BOUND').reduce((s,l)=>s+l.usedSeats,0)}</p>
          <p className="text-xs text-gray-500 mt-0.5">Seat ที่ผูกกับ User</p>
        </div>
      </div>

      <div className="flex gap-3 mb-5">
        <div className="relative flex-1">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"/>
          <input value={search} onChange={e=>setSearch(e.target.value)} className="w-full pl-9 pr-3 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white" placeholder="ค้นหาชื่อโปรแกรม, license key, ชื่อ asset..."/>
        </div>
        <select value={filterCompany} onChange={e=>setFilterCompany(e.target.value)} className="border border-gray-200 rounded-xl px-3 py-2.5 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-purple-400">
          <option value="">ทุกบริษัท</option>
          {companies.map(c=><option key={c.id} value={c.id}>{c.name}</option>)}
        </select>
        <select value={filterType} onChange={e=>setFilterType(e.target.value)} className="border border-gray-200 rounded-xl px-3 py-2.5 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-500">
          <option value="">ทุกประเภท</option>
          <option value="ASSET_BOUND">Asset Bound</option>
          <option value="USER_BOUND">User Bound</option>
        </select>
      </div>

      {isLoading && <div className="flex justify-center py-10 text-gray-400"><RefreshCw size={20} className="animate-spin"/></div>}

      {!isLoading && (
        <div className="space-y-6">
          {(!filterType||filterType==='ASSET_BOUND') && (()=>{
            const groups = groupLicenses(assetBound)
            return (
            <section>
              <h2 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3 flex items-center gap-2">
                <Monitor size={13}/> Asset Bound — ผูกกับเครื่อง ({groups.length} กลุ่ม · {assetBound.length} license · {assetBound.reduce((s,l)=>s+l.usedSeats,0)}/{assetBound.reduce((s,l)=>s+(l.seats||0),0)} seats ใช้แล้ว)
              </h2>
              {groups.length===0?<div className="text-center text-gray-400 py-6 text-sm border-2 border-dashed border-gray-100 rounded-xl">ไม่มีรายการ</div>
                :<div className="space-y-2">{groups.map(g=>(
                  <LicenseGroupCard key={g.key} group={g} isAdmin={isAdmin}
                    onEdit={l=>setEditItem(l)}
                    onDelete={l=>{if(confirm(`ลบ ${l.name}?`))deleteMut.mutate(l.id)}}
                    forceExpand={g.licenses.some(l=>assetMatchedIds.has(l.id))}
                    highlightAsset={g.licenses.some(l=>assetMatchedIds.has(l.id)) ? search : undefined}
                  />
                ))}</div>}
            </section>
          )})()}
          {(!filterType||filterType==='USER_BOUND') && (()=>{
            const groups = groupLicenses(userBound)
            return (
            <section>
              <h2 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3 flex items-center gap-2">
                <User size={13}/> User Bound — ผูกกับบุคคล ({groups.length} กลุ่ม · {userBound.length} license · {userBound.reduce((s,l)=>s+l.usedSeats,0)}/{userBound.reduce((s,l)=>s+(l.seats||0),0)} seats ใช้แล้ว)
              </h2>
              {groups.length===0?<div className="text-center text-gray-400 py-6 text-sm border-2 border-dashed border-gray-100 rounded-xl">ไม่มีรายการ</div>
                :<div className="space-y-2">{groups.map(g=>(
                  <LicenseGroupCard key={g.key} group={g} isAdmin={isAdmin}
                    onEdit={l=>setEditItem(l)}
                    onDelete={l=>{if(confirm(`ลบ ${l.name}?`))deleteMut.mutate(l.id)}}
                  />
                ))}</div>}
            </section>
          )})()}
        </div>
      )}

      {showForm && <LicenseModal allLicenses={licenses} onClose={()=>setShowForm(false)} onSave={d=>createMut.mutate(d)}/>}
      {editItem  && <LicenseModal license={licenses.find(l=>l.id===editItem.id)||editItem} onClose={()=>setEditItem(null)} onSave={d=>updateMut.mutate({id:editItem.id,...d})}/>}
    </div>
  )
}
